import type { SourceRef } from "../contract/types";
import { Icon } from "../components/Icon";
import type { SourceData } from "../api/client";

/**
 * Rail derecho: las fuentes del turno.
 *
 * Es la trazabilidad visible de la respuesta, y va separada en dos bloques
 * porque la distinción es la que importa: lo que está en el sílabo (y por
 * tanto se evalúa) frente a lo que viene de fuera. Ordena igual que el átomo
 * `source_list`, para que el rail y los bloques no se contradigan.
 */

export interface SourceRailProps {
  /** Fuentes citadas por el último turno del asistente. */
  cited: SourceRef[];
  /** Índice del bundle, para dar contexto cuando no hay citas. */
  index: SourceData[];
  /**
   * Conceptos totales del bundle. `null` cuando el backend no lo informa: en
   * ese caso NO se afirma ningún total, porque `index.length` es solo el
   * tamaño de la página pedida y presentarlo como el total sería falso.
   */
  total: number | null;
  loading: boolean;
}

function isOutside(source: SourceRef): boolean {
  return source.kind === "outside";
}

export function SourceRail({ cited, index, total, loading }: SourceRailProps) {
  const course = cited.filter((source) => !isOutside(source));
  const outside = cited.filter(isOutside);

  return (
    <div className="rail">
      <header className="rail__head">
        <h2 className="rail__title">
          <Icon name="book" size="0.875rem" />
          Fuentes del turno
        </h2>
        <span className="rail__count u-nums">{cited.length}</span>
      </header>

      {cited.length === 0 ? (
        <div className="rail__empty">
          <p>
            {loading
              ? "Cargando el índice del bundle…"
              : "Todavía no hay fuentes citadas en este turno."}
          </p>
          {index.length > 0 ? (
            <>
              <p className="rail__empty-note">
                {total !== null
                  ? `El bundle tiene ${total} documentos del curso. Aparecerán aquí en cuanto el asistente los cite.`
                  : "Aparecerán aquí en cuanto el asistente los cite."}
              </p>
              <ul className="rail__preview">
                {index.slice(0, 6).map((source) => (
                  <li key={source.path} className="rail__preview-item">
                    <Icon name="book" size="0.75rem" />
                    <span>{source.title ?? source.path}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : (
        <>
          <RailGroup title="Del curso" sources={course} />
          {outside.length > 0 ? (
            <RailGroup title="Fuera del sílabo" sources={outside} tone="outside" />
          ) : null}
        </>
      )}
    </div>
  );
}

function RailGroup({
  title,
  sources,
  tone,
}: {
  title: string;
  sources: SourceRef[];
  tone?: "outside";
}) {
  if (sources.length === 0) return null;
  return (
    <section className={`rail__group ${tone ? `rail__group--${tone}` : ""}`}>
      <h3 className="rail__group-title">
        {title}
        <span className="rail__count u-nums">{sources.length}</span>
      </h3>
      <ul className="rail__list">
        {sources.map((source, index) => (
          <li key={`${source.path}-${index}`} className="rail__item">
            <div className="rail__item-head">
              <span className="rail__item-title">{source.title ?? source.path}</span>
              {source.page !== null && source.page !== undefined ? (
                <span className="rail__item-page u-nums">p. {source.page}</span>
              ) : null}
            </div>
            <p className="rail__item-path u-mono">{source.path}</p>
            {source.snippet ? <p className="rail__item-snippet">{source.snippet}</p> : null}
            <div className="rail__badges">
              {source.trust ? (
                <span className={`rail__badge rail__badge--${source.trust}`}>
                  {source.trust === "human-reviewed"
                    ? "Revisado"
                    : source.trust === "machine-confirmed"
                      ? "Verificado"
                      : "Sin verificar"}
                </span>
              ) : null}
              {source.stale ? <span className="rail__badge rail__badge--stale">Desactualizado</span> : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
